import {
  type CombinedError,
  mutateManager,
  queryManager,
} from '@pypestream/api-services/urql';
import type {
  SecretScopeType,
  SettingsGroupView,
  SettingsOverride,
  SettingsValues,
} from '@pypestream/ui-patterns';

import { MaestroRequestError, reasonOf } from './maestro-error';

export type { MaestroError, MaestroFailure } from './maestro-error';
export { MaestroRequestError, reasonFor } from './maestro-error';

// Hand-written until these admin_ fields are on the codegen endpoint; then they
// move to manager.gql (see queryManager).
const GROUP_FIELDS = `group values lockVersion leaves inherited { values source { scopeType id name root } }`;
const REF = `id name`;
const SECRET_FIELDS = `id name kind description pypestreamKey value set updatedAt
  scope { type project { ${REF} } application { ${REF} } agent { ${REF} } }
  audience { mode accountIds } updatedBy { name pypestream } updatedVia violations`;

export interface NamedRef {
  id: number;
  name: string;
}

/** Where an item lives. Names are read live, so a renamed project shows its new name. */
export interface OrgSecretScope {
  type: SecretScopeType;
  project: NamedRef | null;
  application: NamedRef | null;
  agent: NamedRef | null;
}

export type KeyAudienceMode = 'all' | 'selected';

/** Who receives a Pypestream key: everyone but `accountIds`, or only `accountIds` (and their children). */
export interface KeyAudience {
  mode: KeyAudienceMode;
  accountIds: string[];
}

export interface SecretAuthor {
  /** Null when the user could not be resolved. */
  name: string | null;
  /** Pypestream staff editing a customer org, shown as "Pypestream". */
  pypestream: boolean;
}

export const SECRET_VIOLATIONS = [
  'reserved_prefix',
  'clash',
  'format',
] as const;
export type SecretViolation = (typeof SECRET_VIOLATIONS)[number];

export interface OrgSecret {
  id: number;
  name: string;
  kind: 'secret' | 'variable';
  description: string | null;
  pypestreamKey: boolean;
  /** A variable's text; always null for a secret. */
  value: string | null;
  set: boolean;
  updatedAt: string | null;
  scope: OrgSecretScope;
  audience: KeyAudience | null;
  updatedBy: SecretAuthor | null;
  /** `manager`, `studio`, `builder` or `liveview`; null on rows written before it was recorded. */
  updatedVia: string | null;
  /** Spec 3.9: an existing item breaking today's naming rules, to be replaced. */
  violations: SecretViolation[];
}

export interface InheritedSecret {
  name: string;
  kind: 'secret' | 'variable';
  /** The org that holds it; null when the backend could not map it to an account. */
  accountId: string | null;
  pypestreamKey: boolean;
}

/** What this user may do with the Maestro tabs of one org (OrganizationMetadata). */
export interface MaestroOrgPermissions {
  canViewSettings: boolean;
  canManageSettings: boolean;
  canViewSecrets: boolean;
  canManageSecrets: boolean;
}

export interface OrgSecretScopeInput {
  scopeType: SecretScopeType;
  projectId?: number;
  applicationId?: number;
  agentId?: number;
}

export interface NewOrgSecret {
  name: string;
  kind: 'secret' | 'variable';
  value: string;
  description?: string;
  pypestreamKey?: boolean;
  scope?: OrgSecretScopeInput;
  /** Required with `pypestreamKey`. */
  audience?: KeyAudience;
}

export interface OrgSecretUpdate {
  value?: string;
  description?: string;
  pypestreamKey?: boolean;
  audience?: KeyAudience;
  /** A move; the server re-checks the name at the new scope. */
  scope?: OrgSecretScopeInput;
}

/** One of this org's projects, with what an item can be scoped to inside it. */
export interface ScopeProject extends NamedRef {
  applications: NamedRef[];
  agents: (NamedRef & { applicationId: number | null })[];
}

export interface AudienceOrg {
  accountId: string;
  name: string;
  parentAccountId: string | null;
}

export interface OrgSecrets {
  own: OrgSecret[];
  /** This org's project, application and agent items (spec 6.1), never a child org's. */
  scoped: OrgSecret[];
  inherited: InheritedSecret[];
}

const unwrap = <T>(result: {
  data?: { admin_?: T | null };
  error?: CombinedError;
}): T => {
  if (result.error || !result.data?.admin_) {
    const { reason, existing } = reasonOf(result.error);
    throw new MaestroRequestError(reason, existing);
  }
  return result.data.admin_;
};

const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : {};

const ref = (value: unknown): NamedRef | null => {
  const { id, name } = record(value);
  return typeof id === 'number' && typeof name === 'string'
    ? { id, name }
    : null;
};

const SCOPE_TYPES: readonly SecretScopeType[] = [
  'org',
  'project',
  'application',
  'agent',
];

/**
 * The one place a row from the hand-written query is trusted: fields an older
 * pypestreamAdmin does not send yet read as an org-scoped row with no author.
 */
export function toOrgSecret(raw: unknown): OrgSecret {
  const row = record(raw);
  const scope = record(row.scope);
  const audience = record(row.audience);
  const author = record(row.updatedBy);
  const type = SCOPE_TYPES.find((t) => t === scope.type) ?? 'org';
  const str = (value: unknown) => (typeof value === 'string' ? value : null);
  const violations: unknown[] = Array.isArray(row.violations)
    ? row.violations
    : [];
  return {
    id: Number(row.id),
    name: String(row.name ?? ''),
    kind: row.kind === 'variable' ? 'variable' : 'secret',
    description: str(row.description),
    pypestreamKey: row.pypestreamKey === true,
    value: str(row.value),
    set: row.set !== false,
    updatedAt: str(row.updatedAt),
    scope: {
      type,
      project: ref(scope.project),
      application: ref(scope.application),
      agent: ref(scope.agent),
    },
    audience:
      audience.mode === 'all' || audience.mode === 'selected'
        ? {
            mode: audience.mode,
            accountIds: Array.isArray(audience.accountIds)
              ? audience.accountIds.filter(
                  (id): id is string => typeof id === 'string'
                )
              : [],
          }
        : null,
    updatedBy:
      row.updatedBy == null
        ? null
        : { name: str(author.name), pypestream: author.pypestream === true },
    updatedVia: str(row.updatedVia),
    violations: SECRET_VIOLATIONS.filter((v) => violations.includes(v)),
  };
}

// A mutation answering `false` did nothing, which the caller must not report as done.
const confirmed = (done: boolean) => {
  if (!done) throw new MaestroRequestError('unavailable');
};

export async function fetchOrgGroup(
  accountId: string,
  group: string
): Promise<SettingsGroupView> {
  const result = await queryManager<{
    admin_: { maestroOrgSettings: SettingsGroupView };
  }>(
    `query MaestroOrgSettings($accountId: String!, $group: String!) {
      admin_ { maestroOrgSettings(accountId: $accountId, group: $group) { ${GROUP_FIELDS} } }
    }`,
    { accountId, group }
  );
  return unwrap(result).maestroOrgSettings;
}

export async function fetchOverriddenIn(
  accountId: string,
  group: string
): Promise<SettingsOverride[]> {
  const result = await queryManager<{
    admin_: { maestroOrgSettingsOverriddenIn: SettingsOverride[] };
  }>(
    `query MaestroOrgSettingsOverriddenIn($accountId: String!, $group: String!) {
      admin_ { maestroOrgSettingsOverriddenIn(accountId: $accountId, group: $group) { scopeType name values } }
    }`,
    { accountId, group }
  );
  return unwrap(result).maestroOrgSettingsOverriddenIn;
}

export async function saveOrgGroup({
  accountId,
  group,
  values,
  lockVersion,
}: {
  accountId: string;
  group: string;
  values: SettingsValues;
  lockVersion: number | null;
}): Promise<SettingsGroupView> {
  const result = await mutateManager<{
    admin_: { updateMaestroOrgSettings: SettingsGroupView };
  }>(
    `mutation UpdateMaestroOrgSettings($accountId: String!, $group: String!, $values: JSON!, $lockVersion: Int) {
      admin_ { updateMaestroOrgSettings(accountId: $accountId, group: $group, values: $values, lockVersion: $lockVersion) { ${GROUP_FIELDS} } }
    }`,
    { accountId, group, values, lockVersion }
  );
  return unwrap(result).updateMaestroOrgSettings;
}

export async function resetOrgGroup(
  accountId: string,
  group: string,
  lockVersion: number
): Promise<void> {
  const result = await mutateManager<{
    admin_: { resetMaestroOrgSettings: boolean };
  }>(
    `mutation ResetMaestroOrgSettings($accountId: String!, $group: String!, $lockVersion: Int!) {
      admin_ { resetMaestroOrgSettings(accountId: $accountId, group: $group, lockVersion: $lockVersion) }
    }`,
    { accountId, group, lockVersion }
  );
  confirmed(unwrap(result).resetMaestroOrgSettings);
}

export async function fetchOrgSecrets(accountId: string): Promise<OrgSecrets> {
  const result = await queryManager<{
    admin_: {
      maestroOrgSecrets: {
        own: unknown[];
        scoped?: unknown[] | null;
        inherited: InheritedSecret[];
      };
    };
  }>(
    `query MaestroOrgSecrets($accountId: String!) {
      admin_ { maestroOrgSecrets(accountId: $accountId) { own { ${SECRET_FIELDS} } scoped { ${SECRET_FIELDS} } inherited { name kind accountId pypestreamKey } } }
    }`,
    { accountId }
  );
  const { own, scoped, inherited } = unwrap(result).maestroOrgSecrets;
  return {
    own: own.map(toOrgSecret),
    scoped: (scoped ?? []).map(toOrgSecret),
    inherited,
  };
}

export async function createOrgSecret(
  accountId: string,
  input: NewOrgSecret
): Promise<OrgSecret> {
  const result = await mutateManager<{
    admin_: { createMaestroOrgSecret: unknown };
  }>(
    `mutation CreateMaestroOrgSecret($accountId: String!, $input: MaestroOrgSecretInput!) {
      admin_ { createMaestroOrgSecret(accountId: $accountId, input: $input) { ${SECRET_FIELDS} } }
    }`,
    { accountId, input }
  );
  return toOrgSecret(unwrap(result).createMaestroOrgSecret);
}

export async function updateOrgSecret(
  accountId: string,
  id: number,
  input: OrgSecretUpdate
): Promise<OrgSecret> {
  const result = await mutateManager<{
    admin_: { updateMaestroOrgSecret: unknown };
  }>(
    `mutation UpdateMaestroOrgSecret($accountId: String!, $id: Int!, $input: MaestroOrgSecretUpdate!) {
      admin_ { updateMaestroOrgSecret(accountId: $accountId, id: $id, input: $input) { ${SECRET_FIELDS} } }
    }`,
    { accountId, id, input }
  );
  return toOrgSecret(unwrap(result).updateMaestroOrgSecret);
}

export const rotateOrgSecret = (
  accountId: string,
  id: number,
  value: string
): Promise<OrgSecret> => updateOrgSecret(accountId, id, { value });

export async function fetchOrgSecretScopes(
  accountId: string
): Promise<ScopeProject[]> {
  const result = await queryManager<{
    admin_: { maestroOrgSecretScopes: ScopeProject[] };
  }>(
    `query MaestroOrgSecretScopes($accountId: String!) {
      admin_ { maestroOrgSecretScopes(accountId: $accountId) { ${REF} applications { ${REF} } agents { ${REF} applicationId } } }
    }`,
    { accountId }
  );
  return unwrap(result).maestroOrgSecretScopes;
}

export async function fetchAudienceOrgs(
  accountId: string
): Promise<AudienceOrg[]> {
  const result = await queryManager<{
    admin_: { maestroPypestreamKeyAudienceOrgs: AudienceOrg[] };
  }>(
    `query MaestroPypestreamKeyAudienceOrgs($accountId: String!) {
      admin_ { maestroPypestreamKeyAudienceOrgs(accountId: $accountId) { accountId name parentAccountId } }
    }`,
    { accountId }
  );
  return unwrap(result).maestroPypestreamKeyAudienceOrgs;
}

export async function fetchAudienceCount(
  accountId: string,
  audience: KeyAudience
): Promise<number> {
  const result = await queryManager<{
    admin_: { maestroPypestreamKeyAudienceCount: number };
  }>(
    `query MaestroPypestreamKeyAudienceCount($accountId: String!, $audience: MaestroKeyAudienceInput!) {
      admin_ { maestroPypestreamKeyAudienceCount(accountId: $accountId, audience: $audience) }
    }`,
    { accountId, audience }
  );
  return unwrap(result).maestroPypestreamKeyAudienceCount;
}

export async function deleteOrgSecret(
  accountId: string,
  id: number
): Promise<void> {
  const result = await mutateManager<{
    admin_: { deleteMaestroOrgSecret: boolean };
  }>(
    `mutation DeleteMaestroOrgSecret($accountId: String!, $id: Int!) {
      admin_ { deleteMaestroOrgSecret(accountId: $accountId, id: $id) }
    }`,
    { accountId, id }
  );
  confirmed(unwrap(result).deleteMaestroOrgSecret);
}

export async function fetchMaestroOrgPermissions(
  accountId: string
): Promise<MaestroOrgPermissions> {
  const result = await queryManager<{
    admin_: {
      organizationMetadata: {
        canViewMaestroSettings: boolean | null;
        canManageMaestroSettings: boolean | null;
        canViewMaestroSecrets: boolean | null;
        canManageMaestroSecrets: boolean | null;
      } | null;
    };
  }>(
    `query MaestroOrgPermissions($accountId: String!) {
      admin_ { organizationMetadata(accountId: $accountId) { canViewMaestroSettings canManageMaestroSettings canViewMaestroSecrets canManageMaestroSecrets } }
    }`,
    { accountId }
  );
  const meta = unwrap(result).organizationMetadata;
  return {
    canViewSettings: Boolean(meta?.canViewMaestroSettings),
    canManageSettings: Boolean(meta?.canManageMaestroSettings),
    canViewSecrets: Boolean(meta?.canViewMaestroSecrets),
    canManageSecrets: Boolean(meta?.canManageMaestroSecrets),
  };
}
